import { readPublicShareRoute } from './shareRoute';
import {
  localizePublicSeoPath,
  resolvePublicSeoPagePath,
  type PublicSeoLanguage,
} from '../config/publicSeoPages';
import { SECURITY_TRUST_PATH } from '../config/securityTrustSeo';
import { buildPaymentLinkPath, parsePaymentLinkPath } from '../config/paymentLinkSeo';
import { buildOfferPath, parseOfferPath } from '../config/offerSeo';
import { hasBillingReturn } from './billingReturn';
import { readSharedAnswerSignupReturnPath } from './sharedAnswerSignupNavigation';
import {
  FRENCH_GLOBAL_VIDEO_WATCH_PATH,
  isVideoWatchPath,
  VIDEO_WATCH_PATH,
} from '../config/videoWatchSeo';

export const PUBLIC_HOME_PATH = '/';
export const PUBLIC_FAQ_PATH = `${PUBLIC_HOME_PATH}#faq`;
export const PUBLIC_PRICING_PATH = `${PUBLIC_HOME_PATH}#pricing`;
export const MANIFESTO_PATH = '/manifesto';
export const ASSISTANT_ADR_PATH = '/assistant-ia-adr';
export const CONSULTANT_AGENT_PATH = '/agent-consultant-adr';
export const TRANSPORT_ANALYZER_PATH = '/agent-auditeur-transport';
export const WASTE_OPERATORS_PATH = '/eliminateurs-dechets';
export const AI_RELIABILITY_PATH = '/fiabilite-ia-adr';
export const LOGIN_PATH = '/login';
export const REGISTER_PATH = '/register';
const PUBLIC_AUTH_ENTRY_STATE_KEY = 'palenPublicAuthEntry';
const VIDEO_WATCH_ORIGIN_PARAM = 'from';
export type VideoWatchOrigin = 'palen-presentation-video' | 'transport-auditor-video';
const SEARCH_CRAWLER_USER_AGENT_PATTERN = /(?:bot|crawler|spider|slurp|bingpreview|googleother|google-inspectiontool)/i;
export const TERMS_OF_SERVICE_PATH = '/terms';
export const PRIVACY_POLICY_PATH = '/privacy';
export const COOKIE_POLICY_PATH = '/cookies';
export { SECURITY_TRUST_PATH };
export type PublicPageLanguage = PublicSeoLanguage;
export const ENGLISH_PUBLIC_PATH_PREFIX = '/en';

function currentPathname(): string {
  return typeof window === 'undefined' ? PUBLIC_HOME_PATH : window.location.pathname;
}

function normalizePublicPathname(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

export function readPublicPageLanguage(pathname = currentPathname()): PublicPageLanguage {
  const normalizedPathname = normalizePublicPathname(pathname);
  return normalizedPathname === ENGLISH_PUBLIC_PATH_PREFIX
    || normalizedPathname.startsWith(`${ENGLISH_PUBLIC_PATH_PREFIX}/`)
    ? 'en'
    : 'fr';
}

export function stripPublicLanguagePrefix(pathname: string): string {
  const normalizedPathname = normalizePublicPathname(pathname);
  if (normalizedPathname === ENGLISH_PUBLIC_PATH_PREFIX) {
    return PUBLIC_HOME_PATH;
  }
  if (normalizedPathname.startsWith(`${ENGLISH_PUBLIC_PATH_PREFIX}/`)) {
    return normalizePublicPathname(normalizedPathname.slice(ENGLISH_PUBLIC_PATH_PREFIX.length));
  }
  return normalizedPathname;
}

export function localizePublicPath(pathname: string, language: PublicPageLanguage): string {
  const offerRoute = parseOfferPath(pathname);
  if (offerRoute) return buildOfferPath(offerRoute.offerId, language);
  const paymentRoute = parsePaymentLinkPath(pathname);
  if (paymentRoute) return buildPaymentLinkPath(paymentRoute.priceId, language);
  const basePathname = resolvePublicSeoPagePath(pathname, readPublicPageLanguage(pathname));
  if (!basePathname) {
    return pathname;
  }
  return localizePublicSeoPath(basePathname, language);
}

export function localizePublicHref(href: string, language: PublicPageLanguage): string {
  if (!href.startsWith('/')) {
    return href;
  }

  const parsed = new URL(href, 'https://palen-adr.eu');
  const localizedPathname = localizePublicPath(parsed.pathname, language);
  return `${localizedPathname}${parsed.search}${parsed.hash}`;
}

export function buildVideoWatchAgentHref(href: string, origin: VideoWatchOrigin): string {
  const parsed = new URL(href, 'https://palen-adr.eu');
  parsed.searchParams.set(VIDEO_WATCH_ORIGIN_PARAM, origin);
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

export function resolveAgentProductBackHref(
  search: string,
  language: PublicPageLanguage,
): string {
  const sharedAnswerReturn = readSharedAnswerSignupReturnPath(search);
  if (sharedAnswerReturn) return sharedAnswerReturn;
  switch (new URLSearchParams(search).get(VIDEO_WATCH_ORIGIN_PARAM)) {
    case 'palen-presentation-video':
      return FRENCH_GLOBAL_VIDEO_WATCH_PATH;
    case 'transport-auditor-video':
      return VIDEO_WATCH_PATH;
    default:
      return localizePublicHref(PUBLIC_HOME_PATH, language);
  }
}

export function resolveInitialPublicLanguageRedirect({
  href,
  browserLanguage,
  savedLanguage,
  userAgent,
}: {
  href: string;
  browserLanguage: string;
  savedLanguage: string | null;
  userAgent: string;
}): string | null {
  if (SEARCH_CRAWLER_USER_AGENT_PATTERN.test(userAgent)) {
    return null;
  }

  const parsed = new URL(href, 'https://palen-adr.eu');
  if (parsePaymentLinkPath(parsed.pathname) || parseOfferPath(parsed.pathname) || hasBillingReturn(parsed.href)) return null;

  const preferredLanguage = savedLanguage === 'fr' || savedLanguage === 'en'
    ? savedLanguage
    : browserLanguage.toLowerCase().startsWith('fr') ? 'fr' : 'en';
  const currentLanguage = readPublicPageLanguage(parsed.pathname);

  if (preferredLanguage === currentLanguage) {
    return null;
  }

  const currentHref = `${parsed.pathname}${parsed.search}${parsed.hash}`;
  const localizedHref = localizePublicHref(currentHref, preferredLanguage);
  return localizedHref === currentHref ? null : localizedHref;
}

export function readInitialPublicLanguageRedirect(savedLanguage: string | null): string | null {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return null;
  }

  return resolveInitialPublicLanguageRedirect({
    href: `${window.location.pathname}${window.location.search}${window.location.hash}`,
    browserLanguage: navigator.language,
    savedLanguage,
    userAgent: navigator.userAgent,
  });
}

function currentSearchParams(): string {
  return typeof window === 'undefined' ? '' : window.location.search;
}

function currentHash(): string {
  return typeof window === 'undefined' ? '' : window.location.hash;
}

export function isPublicHomeRoute(pathname = currentPathname()): boolean {
  return stripPublicLanguagePrefix(pathname) === PUBLIC_HOME_PATH;
}

export function isManifestoRoute(pathname = currentPathname()): boolean {
  return stripPublicLanguagePrefix(pathname) === MANIFESTO_PATH;
}

export function isAssistantAdrRoute(pathname = currentPathname()): boolean {
  return stripPublicLanguagePrefix(pathname) === ASSISTANT_ADR_PATH;
}

export function isConsultantAgentRoute(pathname = currentPathname()): boolean {
  return stripPublicLanguagePrefix(pathname) === CONSULTANT_AGENT_PATH;
}

export function isTransportAnalyzerRoute(pathname = currentPathname()): boolean {
  return stripPublicLanguagePrefix(pathname) === TRANSPORT_ANALYZER_PATH;
}

export function isAiReliabilityRoute(pathname = currentPathname()): boolean {
  return stripPublicLanguagePrefix(pathname) === AI_RELIABILITY_PATH;
}

export function isWasteOperatorsRoute(pathname = currentPathname()): boolean {
  return resolvePublicSeoPagePath(pathname, readPublicPageLanguage(pathname)) === WASTE_OPERATORS_PATH;
}

export function isSecurityTrustRoute(pathname = currentPathname()): boolean {
  return stripPublicLanguagePrefix(pathname) === SECURITY_TRUST_PATH;
}

export function isVideoWatchRoute(pathname = currentPathname()): boolean {
  return isVideoWatchPath(normalizePublicPathname(pathname));
}

export function isLoginRoute(pathname = currentPathname()): boolean {
  return normalizePublicPathname(pathname) === LOGIN_PATH;
}

export function isRegisterRoute(pathname = currentPathname()): boolean {
  return normalizePublicPathname(pathname) === REGISTER_PATH;
}

export function readPaymentLinkPriceId(pathname = currentPathname()): string | null {
  return parsePaymentLinkPath(pathname)?.priceId ?? null;
}

export function isPaymentLinkRoute(pathname = currentPathname()): boolean {
  return readPaymentLinkPriceId(pathname) !== null;
}

export function readOfferId(pathname = currentPathname()) {
  return parseOfferPath(pathname)?.offerId ?? null;
}

export function isTermsOfServiceRoute(pathname = currentPathname()): boolean {
  return stripPublicLanguagePrefix(pathname) === TERMS_OF_SERVICE_PATH;
}

export function isPrivacyPolicyRoute(pathname = currentPathname()): boolean {
  return stripPublicLanguagePrefix(pathname) === PRIVACY_POLICY_PATH;
}

export function isCookiePolicyRoute(pathname = currentPathname()): boolean {
  return stripPublicLanguagePrefix(pathname) === COOKIE_POLICY_PATH;
}

export function isPublicShareRoute(pathname = currentPathname()): boolean {
  return readPublicShareRoute(pathname) !== null;
}

export function isKnownPublicRoute(pathname = currentPathname()): boolean {
  return isPublicHomeRoute(pathname)
    || isManifestoRoute(pathname)
    || isAssistantAdrRoute(pathname)
    || isConsultantAgentRoute(pathname)
    || isTransportAnalyzerRoute(pathname)
    || isWasteOperatorsRoute(pathname)
    || isAiReliabilityRoute(pathname)
    || isSecurityTrustRoute(pathname)
    || isVideoWatchRoute(pathname)
    || isLoginRoute(pathname)
    || isRegisterRoute(pathname)
    || isPaymentLinkRoute(pathname)
    || readOfferId(pathname) !== null
    || isTermsOfServiceRoute(pathname)
    || isPrivacyPolicyRoute(pathname)
    || isCookiePolicyRoute(pathname)
    || isPublicShareRoute(pathname);
}

export function navigateToLoginRoute(): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (isLoginRoute()) {
    return;
  }

  window.history.pushState(
    {
      [PUBLIC_AUTH_ENTRY_STATE_KEY]: true,
    },
    '',
    `${LOGIN_PATH}${currentSearchParams()}`,
  );
}

export function navigateToRegisterRoute(): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (isRegisterRoute()) {
    return;
  }

  window.history.pushState(
    {
      [PUBLIC_AUTH_ENTRY_STATE_KEY]: true,
    },
    '',
    `${REGISTER_PATH}${currentSearchParams()}`,
  );
}

export function rememberPublicLandingSection(sectionId: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  const sectionHash = `#${encodeURIComponent(sectionId)}`;
  if (currentHash() === sectionHash) {
    return;
  }

  window.history.replaceState(
    window.history.state,
    '',
    `${currentPathname()}${currentSearchParams()}${sectionHash}`,
  );
}

export function returnFromPublicAuthRoute(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const sharedAnswerReturn = readSharedAnswerSignupReturnPath(currentSearchParams());
  if (sharedAnswerReturn) {
    window.history.replaceState(null, '', sharedAnswerReturn);
    window.dispatchEvent(new PopStateEvent('popstate'));
    return;
  }

  const historyState = window.history.state;
  if (
    historyState
    && typeof historyState === 'object'
    && historyState[PUBLIC_AUTH_ENTRY_STATE_KEY] === true
  ) {
    window.history.back();
    return;
  }

  replaceWithHomeRoute();
}

export function returnFromLegalDocumentRoute(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (window.history.length > 1 && new URL(document.referrer).origin === window.location.origin) {
      window.history.back();
      return;
    }
  } catch {
    // A direct visit has no usable in-app return destination.
  }

  replaceWithHomeRoute();
}

export function replaceWithHomeRoute(): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (isPublicHomeRoute() && currentSearchParams() === '' && currentHash() === '') {
    return;
  }

  window.history.replaceState(
    null,
    '',
    localizePublicPath(PUBLIC_HOME_PATH, readPublicPageLanguage()),
  );
}
